import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Users, Loader2, BadgeCheck, ChevronRight, Globe2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { CommunityGroups } from "@/components/groups/CommunityGroups";
import { CommunitySaved } from "@/components/CommunitySaved";

const TABS = ["following", "groups", "saved"];

const Avatar = ({ s }) => (s.picture ? (
  <img src={s.picture} alt="" className="h-11 w-11 rounded-full object-cover border border-[#27272A]" />
) : (
  <div className="h-11 w-11 rounded-full flex items-center justify-center font-semibold text-white bg-gradient-to-br from-[#2563EB] to-[#1D4ED8]">
    {(s.name || "?").charAt(0).toUpperCase()}
  </div>
));

export const FollowingChip = ({ active, onClick }) => (
  <button data-testid="market-cat-following" onClick={onClick}
    className={`shrink-0 px-3 py-1.5 text-xs rounded-full transition-all border flex items-center gap-1.5 ${
      active ? "bg-[#2563EB] border-[#2563EB] text-white" : "bg-[#1E1E1E] border-[#27272A] text-[#9CA3AF] hover:text-[#F3F4F6]"}`}>
    <Users className="h-3 w-3" /> Following
  </button>
);

const FollowingList = ({ onClose }) => {
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/following").then((r) => setList(r.data)).catch(() => setList([])); }, []);
  if (!list) return <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#6B7280]" /></div>;
  if (!list.length) return (
    <div data-testid="following-empty" className="px-6 py-16 text-center">
      <div className="text-sm font-medium text-[#F3F4F6] mb-1">You're not following anyone yet</div>
      <div className="text-xs text-[#9CA3AF]">Open a seller's shop and tap Follow to see them here.</div>
    </div>
  );
  return (
    <ul className="divide-y divide-[#1E1E1E]">
      {list.map((s) => (
        <li key={s.seller_id}>
          <Link to={`/seller/${s.seller_id}`} onClick={onClose} data-testid={`following-item-${s.seller_id}`}
            className="flex items-center gap-3 px-5 py-3 hover:bg-[#121212] transition-colors">
            <Avatar s={s} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1 text-sm font-semibold truncate">
                <span className="truncate">{s.name}</span>
                {s.verified && <BadgeCheck className="h-3.5 w-3.5 text-[#10B981] shrink-0" />}
              </div>
              <div className="text-[11px] text-[#9CA3AF]">{s.product_count} {s.product_count === 1 ? "listing" : "listings"}</div>
            </div>
            <ChevronRight className="h-4 w-4 text-[#6B7280]" />
          </Link>
        </li>
      ))}
    </ul>
  );
};

const Tab = ({ id, tab, setTab, children }) => (
  <button type="button" data-testid={`community-tab-${id}`} onClick={() => setTab(id)}
    className={`flex-1 h-9 rounded-full text-sm transition-colors ${tab === id ? "bg-[#2563EB] text-white" : "text-[#9CA3AF] hover:text-white"}`}>
    {children}
  </button>
);

// Opens on the Groups tab when the URL carries ?community=groups.
export const CommunitySheet = ({ onBuy, owned }) => {
  const [sp, setSp] = useSearchParams();
  const wanted = sp.get("community");
  const [open, setOpen] = useState(!!wanted);
  const [tab, setTab] = useState(TABS.includes(wanted) ? wanted : "following");
  useEffect(() => { if (wanted) { setTab(TABS.includes(wanted) ? wanted : "following"); setOpen(true); } }, [wanted]);
  const change = (v) => {
    setOpen(v);
    if (!v && wanted) { const n = new URLSearchParams(sp); n.delete("community"); setSp(n, { replace: true }); }
  };

  return (
    <Sheet open={open} onOpenChange={change}>
      <SheetTrigger asChild>
        <button type="button" data-testid="market-community-btn" aria-label="Community"
          className="h-8 shrink-0 rounded-full border border-[#27272A] bg-[#1E1E1E] px-2.5 sm:px-3 text-xs text-[#D1D5DB] hover:text-white flex items-center gap-1.5 transition-colors">
          <Globe2 className="h-3.5 w-3.5" /><span className="hidden sm:inline">Community</span>
        </button>
      </SheetTrigger>
      <SheetContent side="right" data-testid="community-sheet" className="bg-[#0A0A0A] border-[#1E1E1E] text-[#F3F4F6] w-[88vw] sm:max-w-sm p-0 overflow-y-auto">
        <SheetHeader className="px-5 py-4 border-b border-[#1E1E1E] space-y-3">
          <SheetTitle className="font-heading text-[#F3F4F6] flex items-center gap-2"><Globe2 className="h-4 w-4 text-[#3B82F6]" /> Community</SheetTitle>
          <div className="flex gap-1 p-1 rounded-full bg-[#121212] border border-[#1E1E1E]">
            <Tab id="following" tab={tab} setTab={setTab}>Following</Tab>
            <Tab id="groups" tab={tab} setTab={setTab}>Groups</Tab>
            <Tab id="saved" tab={tab} setTab={setTab}>Saved</Tab>
          </div>
        </SheetHeader>
        {open && (tab === "following" ? <FollowingList onClose={() => change(false)} />
          : tab === "saved" ? <CommunitySaved onClose={() => change(false)} onBuy={onBuy} owned={owned} />
          : <CommunityGroups onClose={() => change(false)} />)}
      </SheetContent>
    </Sheet>
  );
};
